import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useSession } from "@/services/auth";
import type { PaymentMethodType } from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

export class NetPreparationRequiredError extends Error {}

interface ProcessPaymentResponse {
  success: boolean;
  paymentStatus: string;
  preparationStatus?: "COMPLETE" | "PENDING" | "FAILED";
  paymentIntentId?: string;
  transactionId?: string;
  remainingCreditBalance?: number;
}

export function useProcessPayment({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: ProcessPaymentResponse) => void;
  onError?: (error: Error) => void;
} = {}) {
  const { t } = useTranslation("app");
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      paymentMethodId,
      method,
      paymentTermsId,
      amountCents,
      shipmentIds,
      addressChangeRequestId,
      returnTransportId,
    }: {
      orderId: string;
      paymentMethodId?: string;
      method?: PaymentMethodType;
      paymentTermsId?: string;
      amountCents?: number;
      shipmentIds?: string[];
      addressChangeRequestId?: string;
      returnTransportId?: string | null;
    }) => {
      if (method === "INVOICE") {
        try {
          const { data } = await axios.post<{
            success: boolean;
            error?: string;
            data?: { order?: { paymentStatus?: string } };
          }>(
            `${API_URL}/api/v2/orders/${orderId}/net-terms-payment`,
            { paymentTermsId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (
            !data.success ||
            data.data?.order?.paymentStatus !== "CREDIT_APPROVED"
          ) {
            throw new Error(data.error || t("payOrder.netApprovalUnconfirmed"));
          }
          return {
            success: true,
            paymentStatus: data.data.order.paymentStatus,
          };
        } catch (error) {
          if (
            axios.isAxiosError<{
              error?: unknown;
              message?: unknown;
              code?: unknown;
            }>(error)
          ) {
            const body = error.response?.data;
            const reason =
              typeof body?.message === "string"
                ? body.message
                : typeof body?.error === "string"
                  ? body.error
                  : undefined;
            if (body?.code === "NET_PREPARATION_REQUIRES_SUPPORT") {
              throw new NetPreparationRequiredError(
                reason || t("payOrder.preparationNeedsSupportBody")
              );
            }
            if (reason) throw new Error(reason);
          }
          throw error;
        }
      }

      const { data } = await axios.post<ProcessPaymentResponse>(
        `${API_URL}/api/v2/orders/${orderId}/process-payment`,
        {
          paymentMethodId,
          method,
          amountCents,
          shipmentIds,
          addressChangeRequestId,
          ...(returnTransportId !== undefined ? { returnTransportId } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: (data) => {
      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
    onSettled: (_data, error, variables) => {
      if (error && variables.method !== "INVOICE") return;
      // NET approval can commit even if later Shipment preparation fails.
      void queryClient.invalidateQueries({ queryKey: ["customerOrders"] });
      void queryClient.invalidateQueries({ queryKey: ["customerOrdersV2"] });
      void queryClient.invalidateQueries({ queryKey: ["orderPayments"] });
      void queryClient.invalidateQueries({ queryKey: ["myPaymentTerms"] });
    },
  });
}
